import React, { useState } from 'react';
import { Box, Card, IconButton, Stack, Typography, Collapse } from '@mui/material';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { IAudition, IRole } from '../../types/global/helper';
import { DropdownSelect } from '../DropdownSelect/DropdownSelect';
import CustomButton from '../Button/CustomButton';
import { CheckCircleOutline, ExpandMore } from '@mui/icons-material';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { AuditionList } from './AuditionList';
import { useDispatch } from 'react-redux';
import { setRole } from '../../redux/audition/actions';
import { LinkBox } from '../LinkBox';
import { generateShareLink } from '../../utils/AppUtils';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { CompanyCard } from '../Cards/CompanyCard';
import { useAuth } from '../../hook/useAuth';

export interface IAuditionSuggestion {
    audition: IAudition;
    disableSuggestion?: boolean;
    similarAuditions?: IAudition[];
    hideDetails?: boolean;
}

const AuditionSuggestion: React.FC<IAuditionSuggestion> = ({
    audition,
    disableSuggestion,
    similarAuditions,
    hideDetails,
}) => {
    const { themeStyle, device } = useThemeContext();
    const { roles: auditionRoles } = audition;
    const roles = auditionRoles.filter(role => role != null);
    const dispatch = useDispatch();
    const [selectedRole, setSelectedRole] = useState<IRole | undefined>();
    const [linkBoxExpanded, setLinkBoxExpanded] = useState(false);
    const navigate = useNavigate();

    const { user_id } = useAuth();

    const isExternalUser = user_id !== audition.created_by._id;

    const toggleLinkBox = () => {
        setLinkBoxExpanded(!linkBoxExpanded);
    };

    return (
        <React.Fragment>
            <Stack direction="column">
                {
                    <Card
                        sx={{
                            width: '100%',
                            minWidth: '350px',
                            borderRadius: 2,
                            overflow: 'visible',
                            borderTop: `4px solid ${theme[themeStyle].highlight}`,
                            my: 2,
                            p: 2,
                            position: 'relative',
                            backgroundColor: theme[themeStyle].secondaryBg,
                        }}>
                        <IconButton
                            sx={{
                                position: 'absolute',
                                top: 0,
                                right: 20,
                                borderRadius: 0,
                                borderBottomLeftRadius: '50%',
                                borderBottomRightRadius: '50%',
                                p: 0.5,
                                backgroundColor: theme[themeStyle].highlight,
                            }}>
                            <CheckCircleOutline sx={{ color: theme[themeStyle].true_white, fontSize: 24 }} />
                        </IconButton>
                        <DropdownSelect
                            data={roles.map(r => r.character)}
                            label="Ready to Apply ?"
                            onOptionSelected={(_, index) => {
                                const getRole = roles.find((_, i) => i === index);
                                setSelectedRole(getRole);
                            }}
                            selectProps={{
                                value: selectedRole ? selectedRole.character : '',
                                placeholder: 'Choose a role',
                            }}
                            labelstyle={{
                                fontSize: 16,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                            }}
                        />
                        {isExternalUser && (
                            <CustomButton
                                label={moment(audition.casting_expiration).isBefore(moment()) ? 'Closed' : 'Apply'}
                                sx={{ width: '100%', mt: 1 }}
                                disabled={!selectedRole || moment(audition.casting_expiration).isBefore(moment())}
                                withAuthentication
                                onClick={() => {
                                    dispatch(setRole(selectedRole));
                                }}
                            />
                        )}

                        {!device.isDesktop && (
                            <>
                                <Box sx={{ mt: 2 }}>
                                    <CustomButton
                                        label="Get shareable link"
                                        onClick={toggleLinkBox}
                                        buttonProps={{ variant: 'text' }}
                                        customStyle={{
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '10px 16px',
                                        }}
                                        withIcon
                                        customIcon={
                                            <ExpandMore
                                                sx={{
                                                    transform: linkBoxExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                    transition: 'transform 0.3s ease',
                                                    color: theme[themeStyle].tag_white,
                                                }}
                                            />
                                        }
                                    />
                                    <Collapse in={linkBoxExpanded} timeout="auto" unmountOnExit>
                                        <Box sx={{ mt: 1 }}>
                                            <LinkBox link={generateShareLink('audition', audition, true)} />
                                        </Box>
                                    </Collapse>
                                </Box>
                                {!hideDetails && (
                                    <CustomButton
                                        label="View more details"
                                        onClick={() => navigate(`/audme/${audition.slug}`)}
                                        customStyle={{ marginTop: '10px', width: '100%' }}
                                        sx={{
                                            '&:hover': {
                                                WebkitTextFillColor: theme[themeStyle].primary_orange,
                                            },
                                        }}
                                    />
                                )}
                            </>
                        )}
                    </Card>
                }
                {!disableSuggestion && Array.isArray(similarAuditions) && similarAuditions.length > 0 && (
                    <>
                        <Card
                            sx={{
                                width: '100%',
                                borderRadius: 2,
                                overflow: 'visible',
                                p: 0,
                                minHeight: 'fit-content',
                                backgroundColor: theme[themeStyle].secondaryBg,
                            }}>
                            <Box borderBottom={1}>
                                <Box px={2} py={1}>
                                    <Typography
                                        style={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 19,
                                            color: theme[themeStyle].tag_white,
                                        }}>
                                        Similar Calls
                                    </Typography>
                                </Box>
                            </Box>
                            <AuditionList auditions={similarAuditions!} />
                        </Card>
                    </>
                )}
                {device.isDesktop && (
                    <>
                        <LinkBox link={generateShareLink('audition', audition, true)} sx={{ mt: 2 }} />
                        {!hideDetails && (
                            <CustomButton
                                label="View more details"
                                onClick={() => navigate(`/audme/${audition.slug}`)}
                                customStyle={{ marginTop: '10px' }}
                                sx={{
                                    '&:hover': {
                                        WebkitTextFillColor: theme[themeStyle].primary_orange,
                                    },
                                }}
                            />
                        )}
                    </>
                )}
                {hideDetails && device.isDesktop && (
                    <>
                        <CompanyCard data={audition.production_company} enableHeader />
                    </>
                )}
            </Stack>
        </React.Fragment>
    );
};

export { AuditionSuggestion };
